import type { ChartCandle } from '../chart.typings'
import {
    type IndicatorWorkerCandles,
    type IndicatorWorkerColumns,
    type IndicatorWorkerEvaluateRequest,
    type IndicatorWorkerRequest,
    type IndicatorWorkerResponse,
    type RecordedIndicatorCall,
} from './indicatorWorker.types'

/** A script that hangs this long is treated as unsupported and the worker is recycled. */
const EVALUATION_TIMEOUT_MS = 3000

/** Most candles a tick ships before a full window is cheaper to reason about than an upsert. */
const MAX_TAIL_CANDLES = 50

type PendingEvaluation = {
    script: string
    streamId: string
    resolve: (calls: RecordedIndicatorCall[] | null) => void
    timer: ReturnType<typeof setTimeout>
}

/** What the worker already holds per stream, so the next tick can send only the changed tail. */
type StreamState = { fields: string, lastTime: number, epoch: number }
const streams = new Map<string, StreamState>()

let worker: Worker | null | undefined
/** False until the worker answers once: timeouts before that are module loading, not a slow script. */
let workerResponded = false
let nextId = 1
const pending = new Map<number, PendingEvaluation>()
/** Scripts that failed in the worker once (network, cross-indicator inputs, errors) stay on the main thread. */
const mainThreadScripts = new Set<string>()

function settle(id: number, calls: RecordedIndicatorCall[] | null, keepOnMainThread = !calls) {
    const entry = pending.get(id)
    if (!entry) return
    pending.delete(id)
    clearTimeout(entry.timer)
    if (keepOnMainThread) {
        mainThreadScripts.add(entry.script)
    }
    entry.resolve(calls)
}

function resetWorker() {
    worker?.terminate()
    worker = undefined
    workerResponded = false
    // A new worker starts empty: every stream sends a full window next time.
    streams.clear()
    // Other in-flight scripts did nothing wrong: fall back for this tick only.
    Array.from(pending.keys()).forEach((id) => settle(id, null, false))
}

function getWorker(): Worker | null {
    if (worker !== undefined) return worker
    try {
        worker = new Worker(new URL('./indicatorScript.worker.ts', import.meta.url), { type: 'module' })
        worker.onmessage = (event: MessageEvent<IndicatorWorkerResponse>) => {
            const response = event.data
            workerResponded = true
            if (!response.ok && response.resync) {
                // The cached window drifted: draw this tick on the main thread, send a full window next tick.
                const entry = pending.get(response.id)
                if (entry) streams.delete(entry.streamId)
                settle(response.id, null, false)
                return
            }
            settle(response.id, response.ok ? response.calls : null)
        }
        worker.onerror = () => {
            // The worker failed to load or crashed: run everything on the main thread from now on.
            resetWorker()
            worker = null
        }
    } catch {
        worker = null
    }
    return worker
}

/** Primitive-valued fields of the first and last candle (fields can appear mid-history, e.g. OI or greeks). */
function candleFields(candles: ChartCandle[]): string[] {
    const fields = new Set<string>()
    for (const candle of [candles[0], candles[candles.length - 1]]) {
        if (!candle) continue
        for (const [field, value] of Object.entries(candle)) {
            if (value === null || value === undefined || typeof value !== 'object') {
                fields.add(field)
            }
        }
    }
    return Array.from(fields)
}

function toColumns(candles: ChartCandle[], fields: string[]): IndicatorWorkerColumns {
    const columns: IndicatorWorkerColumns = {}
    for (const field of fields) {
        columns[field] = candles.map((candle) => (candle as unknown as Record<string, unknown>)[field])
    }
    return columns
}

/**
 * The whole window on first use or after a data reload (`epoch` changes); otherwise only the candles from the
 * worker's last one onwards, plus the bar before the latest in case it was finalised late.
 */
function candlesUpdate(streamId: string, candles: ChartCandle[], epoch: number): IndicatorWorkerCandles {
    const fields = candleFields(candles)
    const fieldsKey = fields.join(',')
    const known = streams.get(streamId)
    const length = candles.length
    streams.set(streamId, { fields: fieldsKey, lastTime: candles[length - 1].time as number, epoch })
    if (known && known.fields === fieldsKey && known.epoch === epoch && length >= 2) {
        let start = length - 1
        while (start > 0 && (candles[start].time as number) > known.lastTime) start--
        start = Math.min(start, length - 2)
        if (length - start <= MAX_TAIL_CANDLES) {
            return { kind: 'tail', columns: toColumns(candles.slice(start), fields), firstTime: candles[0].time as number, length }
        }
    }
    return { kind: 'full', columns: toColumns(candles, fields) }
}

/** Frees the worker's candle window for an indicator that was removed from its chart. */
export function disposeIndicatorWorkerStream(streamId: string) {
    if (!streams.delete(streamId) || !worker) return
    try {
        worker.postMessage({ type: 'dispose', streamId } satisfies IndicatorWorkerRequest)
    } catch {
        // Nothing to free if the worker is gone.
    }
}

export function canEvaluateIndicatorInWorker(script: string): boolean {
    return !mainThreadScripts.has(script) && !script.includes('series.fetch') && getWorker() !== null
}

/**
 * Runs `script` in the indicator worker on a live tick. Resolves with the recorded plot calls (each series trimmed
 * to its latest point), or null when the script must run on the main thread instead (the caller then falls back
 * to createIndicatorData). `streamId` names the indicator's cached candle window; bump `epoch` whenever earlier
 * candles may have changed (history load, backfill) so the full window is sent again.
 */
export function evaluateIndicatorInWorker(
    params: Omit<IndicatorWorkerEvaluateRequest, 'type' | 'id' | 'candles'> & { candles: ChartCandle[], epoch: number }
): Promise<RecordedIndicatorCall[] | null> {
    const target = getWorker()
    if (!target || !params.candles.length) return Promise.resolve(null)
    const { candles, epoch, ...rest } = params
    const update = candlesUpdate(params.streamId, candles, epoch)
    const id = nextId++
    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            if (!workerResponded) {
                // Still loading: run this tick on the main thread and keep the worker.
                settle(id, null, false)
                return
            }
            settle(id, null)
            resetWorker()
        }, EVALUATION_TIMEOUT_MS)
        pending.set(id, { script: params.script, streamId: params.streamId, resolve, timer })
        try {
            target.postMessage({ ...rest, type: 'evaluate', id, candles: update } satisfies IndicatorWorkerRequest)
        } catch {
            streams.delete(params.streamId)
            settle(id, null)
        }
    })
}
